import { Button, Checkbox, Modal, Select, Tabs, Tooltip } from 'antd'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import { TYPE_LABELS } from '@/constants'
import type {
  CharacteristicValueType,
  UnitOfMeasurementInterface,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { fetchUnitsOfMeasurement } from '@/utils/api/fetch/fetchEntities'
import {
  APPROVAL_STATUSES,
  type ApprovalStatus,
  CHARACTERISTIC_STATUS_LABELS,
  getStatusBadge,
  isApprovedStatus,
} from '@/utils/catalog/approvalStatus'
import { confirmSlottedImpact } from '@/utils/search/characteristics/slottedImpactPreview'

import CharacteristicCategoryBindingsModal from '../CharacteristicCategoryBindingsModal/CharacteristicCategoryBindingsModal'
import type { BoundCharacteristicCategory } from '../characteristicCategoryUtils'
import {
  fetchCharacteristicDetails,
  invalidateCharacteristicDetails,
} from '../characteristicDetailsFetcher'
import CharacteristicSlotConfigSection, {
  type CharacteristicSlotConfigSectionHandle,
} from '../CharacteristicSlotConfigSection/CharacteristicSlotConfigSection'
import cn from '../DictionaryDetailsModal.module.scss'

type FormActionKey = '' | 'save'

type CharacteristicRecord = {
  id: string
  code: number
  description: string
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  valueType: CharacteristicValueType
  useUnitOfMeasurement: boolean
  unitOfMeasurementDto?: UnitOfMeasurementInterface | null
}

type CharacteristicValue = {
  id: string
  value: string
}

type CharacteristicDetailsModalProps = {
  open: boolean
  item: CharacteristicRecord | null
  onClose: () => void
  onChanged: () => Promise<void> | void
  onLocalUpdate?: (item: CharacteristicRecord) => void
  isApprover: boolean
  readOnly?: boolean
}

type CharacteristicDetailsCacheEntry = {
  description: string
  useUnitOfMeasurement: boolean
  unitOfMeasurementId: string
  status: ApprovalStatus
  items: CharacteristicValue[] | null
  categories: BoundCharacteristicCategory[]
}

const characteristicDetailsCache = new Map<
  string,
  CharacteristicDetailsCacheEntry
>()
let unitsCache: UnitOfMeasurementInterface[] | null = null

const STATUS_SELECT_CLASS: Record<ApprovalStatus, string> = {
  DRAFT: cn.statusSelectDraft,
  APPROVED: cn.statusSelectApproved,
  REJECTED: cn.statusSelectRejected,
}

function CharacteristicDetailsModal(props: CharacteristicDetailsModalProps) {
  const {
    open,
    item,
    onClose,
    onChanged,
    onLocalUpdate,
    isApprover,
    readOnly = false,
  } = props

  const [description, setDescription] = useState('')
  const [useUnitOfMeasurement, setUseUnitOfMeasurement] = useState(false)
  const [unitOfMeasurementId, setUnitOfMeasurementId] = useState('')
  const [status, setStatus] = useState<ApprovalStatus>('DRAFT')
  const [initialStatus, setInitialStatus] = useState<ApprovalStatus>('DRAFT')
  const [units, setUnits] = useState<UnitOfMeasurementInterface[]>([])
  const [items, setItems] = useState<CharacteristicValue[]>([])
  const [itemsLoaded, setItemsLoaded] = useState(false)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [activeTabKey, setActiveTabKey] = useState('categories')
  const [valueSearch, setValueSearch] = useState('')
  const [categories, setCategories] = useState<BoundCharacteristicCategory[]>(
    []
  )
  const [initialCategories, setInitialCategories] = useState<
    BoundCharacteristicCategory[]
  >([])
  const [newValue, setNewValue] = useState('')
  const [editingValueId, setEditingValueId] = useState('')
  const [editingValue, setEditingValue] = useState('')
  const [valueActionKey, setValueActionKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [actionKey, setActionKey] = useState<FormActionKey>('')
  const [initialDescription, setInitialDescription] = useState('')
  const [initialUseUnitOfMeasurement, setInitialUseUnitOfMeasurement] =
    useState(false)
  const [initialUnitOfMeasurementId, setInitialUnitOfMeasurementId] =
    useState('')
  const [categoryTagsOverflowing, setCategoryTagsOverflowing] = useState(false)
  const [canScrollCategoryTagsLeft, setCanScrollCategoryTagsLeft] =
    useState(false)
  const [canScrollCategoryTagsRight, setCanScrollCategoryTagsRight] =
    useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const [isSlotConfigDirty, setIsSlotConfigDirty] = useState(false)
  const hydratedItemIdRef = useRef<string | null>(null)
  const wasOpenRef = useRef(false)
  const categoryTagsScrollRef = useRef<HTMLDivElement | null>(null)
  const slotConfigSectionRef =
    useRef<CharacteristicSlotConfigSectionHandle | null>(null)

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      hydratedItemIdRef.current = null
      return
    }

    if (!item) return

    const shouldHydrate =
      !wasOpenRef.current || hydratedItemIdRef.current !== item.id

    wasOpenRef.current = true

    if (!shouldHydrate) return

    hydratedItemIdRef.current = item.id

    setNewValue('')
    setValueSearch('')
    setEditingValueId('')
    setEditingValue('')
    setIsSlotConfigDirty(false)
    setActiveTabKey('categories')
    setItemsLoading(false)
    const cachedDetails = characteristicDetailsCache.get(item.id)

    if (cachedDetails) {
      setDescription(cachedDetails.description)
      const nextUseUnitOfMeasurement =
        item.valueType !== 'BOOLEAN' && cachedDetails.useUnitOfMeasurement
      setUseUnitOfMeasurement(nextUseUnitOfMeasurement)
      setUnitOfMeasurementId(
        nextUseUnitOfMeasurement ? cachedDetails.unitOfMeasurementId : ''
      )
      setItems(cachedDetails.items ?? [])
      setItemsLoaded(cachedDetails.items !== null)
      setCategories(cachedDetails.categories ?? [])
      setInitialCategories(cachedDetails.categories ?? [])
      setInitialDescription(cachedDetails.description)
      setInitialUseUnitOfMeasurement(nextUseUnitOfMeasurement)
      setInitialUnitOfMeasurementId(
        nextUseUnitOfMeasurement ? cachedDetails.unitOfMeasurementId : ''
      )
      setStatus(cachedDetails.status ?? item.status)
      setInitialStatus(cachedDetails.status ?? item.status)

      if (unitsCache && units.length === 0) {
        setUnits(unitsCache)
      }
      return
    }

    setLoading(true)
    setItems([])
    setItemsLoaded(false)
    setCategories([])
    Promise.all([
      api.get(`/main/characteristics/${item.id}/categories`),
      unitsCache ? Promise.resolve(unitsCache) : fetchUnitsOfMeasurement(),
    ])
      .then(([categoriesResponse, fetchedUnits]) => {
        const details = item
        const nextCategories = categoriesResponse.data.categories ?? []
        const nextUseUnitOfMeasurement =
          item.valueType !== 'BOOLEAN' && details.useUnitOfMeasurement
        setDescription(details.description)
        setUseUnitOfMeasurement(nextUseUnitOfMeasurement)
        setUnitOfMeasurementId(
          nextUseUnitOfMeasurement ? details.unitOfMeasurementDto?.id || '' : ''
        )
        setCategories(nextCategories)
        setInitialCategories(nextCategories)
        setInitialDescription(details.description)
        setInitialUseUnitOfMeasurement(nextUseUnitOfMeasurement)
        setInitialUnitOfMeasurementId(
          nextUseUnitOfMeasurement ? details.unitOfMeasurementDto?.id || '' : ''
        )
        setStatus(details.status)
        setInitialStatus(details.status)

        if (Array.isArray(fetchedUnits)) {
          unitsCache = fetchedUnits
          setUnits(fetchedUnits)
        } else if (unitsCache) {
          setUnits(unitsCache)
        }

        characteristicDetailsCache.set(item.id, {
          description: details.description,
          useUnitOfMeasurement: nextUseUnitOfMeasurement,
          unitOfMeasurementId: nextUseUnitOfMeasurement
            ? details.unitOfMeasurementDto?.id || ''
            : '',
          status: details.status,
          items: null,
          categories: nextCategories,
        })
      })
      .catch((error) => {
        handleApiError(error, modal)
      })
      .finally(() => setLoading(false))
  }, [open, item, units.length, modal])

  useEffect(() => {
    if (
      !open ||
      !item ||
      activeTabKey !== 'values' ||
      itemsLoaded ||
      itemsLoading
    )
      return

    const itemId = item.id
    setItemsLoading(true)
    fetchCharacteristicDetails(itemId)
      .then((response) => {
        if (hydratedItemIdRef.current !== itemId) return
        const nextItems = response.items ?? []
        setItems(nextItems)
        setItemsLoaded(true)
        const cachedDetails = characteristicDetailsCache.get(itemId)
        if (cachedDetails) {
          characteristicDetailsCache.set(itemId, {
            ...cachedDetails,
            items: nextItems,
          })
        }
      })
      .catch((error) => {
        handleApiError(error, modal)
      })
      .finally(() => {
        if (hydratedItemIdRef.current === itemId) setItemsLoading(false)
      })
  }, [open, item, activeTabKey, itemsLoaded, itemsLoading, modal])

  const updateCategoryTagsScrollState = useCallback(() => {
    const scrollNode = categoryTagsScrollRef.current

    if (!scrollNode) {
      setCategoryTagsOverflowing(false)
      setCanScrollCategoryTagsLeft(false)
      setCanScrollCategoryTagsRight(false)
      return
    }

    const maxScrollLeft = Math.max(
      0,
      scrollNode.scrollWidth - scrollNode.clientWidth
    )
    const isOverflowing = maxScrollLeft > 4

    setCategoryTagsOverflowing(isOverflowing)
    setCanScrollCategoryTagsLeft(isOverflowing && scrollNode.scrollLeft > 4)
    setCanScrollCategoryTagsRight(
      isOverflowing && scrollNode.scrollLeft < maxScrollLeft - 4
    )
  }, [])

  useLayoutEffect(() => {
    if (!open) return

    updateCategoryTagsScrollState()
  }, [open, categories, updateCategoryTagsScrollState])

  useEffect(() => {
    if (!open) return

    const scrollNode = categoryTagsScrollRef.current
    const handleWindowResize = () => updateCategoryTagsScrollState()

    window.addEventListener('resize', handleWindowResize)
    scrollNode?.addEventListener('scroll', updateCategoryTagsScrollState)
    updateCategoryTagsScrollState()

    return () => {
      window.removeEventListener('resize', handleWindowResize)
      scrollNode?.removeEventListener('scroll', updateCategoryTagsScrollState)
    }
  }, [open, categories.length, updateCategoryTagsScrollState])

  const splitCategoryRows = useMemo(() => {
    const midpoint = Math.ceil(categories.length / 2)

    return {
      firstRow: categories.slice(0, midpoint),
      secondRow: categories.slice(midpoint),
    }
  }, [categories])

  const filteredItems = useMemo(() => {
    const normalizedSearch = valueSearch.trim().toLowerCase()

    return normalizedSearch
      ? items.filter((value) =>
          value.value.toLowerCase().includes(normalizedSearch)
        )
      : items
  }, [items, valueSearch])

  if (!item) return null

  const isReadOnly = readOnly
  const isLockedApprovedDescription =
    isReadOnly || (isApprovedStatus(item.status) && !isApprover)
  const isLockedApprovedCategories = isReadOnly || isLockedApprovedDescription
  const isLockedApprovedUnitToggle = isReadOnly || isLockedApprovedDescription
  const isReferenceCharacteristic =
    item.valueType === 'REFERENCE' || item.valueType === 'SLOTTED_REFERENCE'
  const isSlottedCharacteristic = item.valueType === 'SLOTTED_REFERENCE'
  const canUseUnitOfMeasurement = item.valueType !== 'BOOLEAN'
  const normalizedDescription = description.trim()
  const normalizedInitialDescription = initialDescription.trim()
  const effectiveUseUnitOfMeasurement =
    canUseUnitOfMeasurement && useUnitOfMeasurement
  const effectiveUnitId = effectiveUseUnitOfMeasurement
    ? unitOfMeasurementId
    : ''
  const initialEffectiveUnitId = initialUseUnitOfMeasurement
    ? initialUnitOfMeasurementId
    : ''
  const initialCategoryIdsKey = [...initialCategories]
    .map((category) => category.id)
    .sort()
    .join(',')
  const currentCategoryIdsKey = [...categories]
    .map((category) => category.id)
    .sort()
    .join(',')
  const isCategoriesDirty = initialCategoryIdsKey !== currentCategoryIdsKey
  const isStatusDirty = status !== initialStatus
  const isDirty =
    normalizedDescription !== normalizedInitialDescription ||
    effectiveUseUnitOfMeasurement !== initialUseUnitOfMeasurement ||
    effectiveUnitId !== initialEffectiveUnitId
  const isBusy = actionKey !== ''

  const updateCacheEntry = ({
    description: nextDescription = initialDescription,
    useUnitOfMeasurement:
      nextUseUnitOfMeasurement = initialUseUnitOfMeasurement,
    unitOfMeasurementId: nextUnitOfMeasurementId = initialUnitOfMeasurementId,
    status: nextStatus = initialStatus,
    nextItems = itemsLoaded ? items : null,
    nextCategories = categories,
  }: {
    description?: string
    useUnitOfMeasurement?: boolean
    unitOfMeasurementId?: string
    status?: ApprovalStatus
    nextItems?: CharacteristicValue[] | null
    nextCategories?: BoundCharacteristicCategory[]
  } = {}) => {
    characteristicDetailsCache.set(item.id, {
      description: nextDescription,
      useUnitOfMeasurement: nextUseUnitOfMeasurement,
      unitOfMeasurementId: nextUnitOfMeasurementId,
      status: nextStatus,
      items: nextItems,
      categories: nextCategories,
    })
  }

  const persistCharacteristic = async () => {
    await api.put(`/main/characteristics/${item.id}`, {
      description: normalizedDescription,
      useUnitOfMeasurement: effectiveUseUnitOfMeasurement,
      unitOfMeasurement: effectiveUseUnitOfMeasurement
        ? unitOfMeasurementId
        : undefined,
    })
    invalidateCharacteristicDetails(item.id)
  }

  const persistCategories = async () => {
    const response = await api.put(
      `/main/characteristics/${item.id}/categories`,
      {
        categoryIds: categories.map((category) => category.id),
      }
    )

    const nextCategories = Array.isArray(response.data.categories)
      ? (response.data.categories as BoundCharacteristicCategory[])
      : categories

    setCategories(nextCategories)
    setInitialCategories(nextCategories)

    return nextCategories
  }

  const handleSave = async () => {
    if (!normalizedDescription) {
      toast.error('Заполните наименование характеристики')
      return
    }

    if (effectiveUseUnitOfMeasurement && !unitOfMeasurementId) {
      toast.error('Выберите единицу измерения')
      return
    }

    if (isSlotConfigDirty) {
      const configs = slotConfigSectionRef.current?.buildConfigsPayload() ?? []
      const decision = await confirmSlottedImpact({
        previewUrl: `/main/characteristics/${item.id}/slot-configs/preview`,
        body: { configs },
        modal,
      })
      if (decision && !decision.proceed) return
    }

    setActionKey('save')
    try {
      let nextCategories = categories

      if (isDirty) {
        await persistCharacteristic()
        setInitialDescription(normalizedDescription)
        setInitialUseUnitOfMeasurement(effectiveUseUnitOfMeasurement)
        setInitialUnitOfMeasurementId(effectiveUnitId)
      }

      if (isCategoriesDirty) {
        nextCategories = await persistCategories()
      }

      if (isSlotConfigDirty) {
        const configs =
          slotConfigSectionRef.current?.buildConfigsPayload() ?? []
        await api.put(`/main/characteristics/${item.id}/slot-configs`, {
          configs,
        })
        slotConfigSectionRef.current?.markSaved()
      }

      if (isStatusDirty) {
        await api.patch(`/main/characteristics/${item.id}/status`, { status })
        setInitialStatus(status)
      }

      updateCacheEntry({
        description: normalizedDescription,
        useUnitOfMeasurement: effectiveUseUnitOfMeasurement,
        unitOfMeasurementId: effectiveUnitId,
        status,
        nextItems: itemsLoaded ? items : null,
        nextCategories,
      })

      const selectedUnit = effectiveUseUnitOfMeasurement
        ? units.find((unit) => unit.id === unitOfMeasurementId)
        : undefined

      onLocalUpdate?.({
        ...item,
        description: normalizedDescription,
        useUnitOfMeasurement: effectiveUseUnitOfMeasurement,
        unitOfMeasurementDto: effectiveUseUnitOfMeasurement
          ? (selectedUnit ?? item.unitOfMeasurementDto)
          : null,
        status,
      })

      toast.success(
        isStatusDirty
          ? status === 'APPROVED'
            ? 'Характеристика утверждена'
            : status === 'REJECTED'
              ? 'Характеристика отклонена'
              : 'Характеристика возвращена на модерацию'
          : 'Характеристика обновлена'
      )

      if (!onLocalUpdate) {
        await onChanged()
      }
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setActionKey('')
    }
  }

  const handleAddValue = async () => {
    const trimmedValue = newValue.trim()

    if (!trimmedValue) {
      toast.error('Заполните значение')
      return
    }

    setValueActionKey('create')
    try {
      const response = await api.post(
        `/main/characteristics/${item.id}/values`,
        {
          value: trimmedValue,
        }
      )

      setItems((prev) => {
        const nextItems = [...prev, response.data]
        updateCacheEntry({ nextItems })
        return nextItems
      })
      invalidateCharacteristicDetails(item.id)
      setNewValue('')
      toast.success('Значение добавлено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setValueActionKey('')
    }
  }

  const handleSaveValue = async () => {
    const trimmedValue = editingValue.trim()

    if (!editingValueId || !trimmedValue) {
      toast.error('Заполните значение')
      return
    }

    setValueActionKey(`update-${editingValueId}`)
    try {
      const response = await api.put(
        `/main/characteristics/values/${editingValueId}`,
        { value: trimmedValue }
      )

      setItems((prev) => {
        const nextItems = prev.map((dictionaryItem) =>
          dictionaryItem.id === editingValueId ? response.data : dictionaryItem
        )
        updateCacheEntry({ nextItems })
        return nextItems
      })
      invalidateCharacteristicDetails(item.id)
      setEditingValueId('')
      setEditingValue('')
      toast.success('Значение обновлено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setValueActionKey('')
    }
  }

  const handleDeleteValue = async (valueId: string) => {
    setValueActionKey(`delete-${valueId}`)
    try {
      await api.delete(`/main/characteristics/values/${valueId}`)
      setItems((prev) => {
        const nextItems = prev.filter(
          (dictionaryItem) => dictionaryItem.id !== valueId
        )
        updateCacheEntry({ nextItems })
        return nextItems
      })
      invalidateCharacteristicDetails(item.id)
      toast.success('Значение удалено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setValueActionKey('')
    }
  }

  const handleRemoveCategory = (categoryId: string) => {
    setCategories((prevCategories) =>
      prevCategories.filter((category) => category.id !== categoryId)
    )
  }

  const renderCategoryTooltip = (category: BoundCharacteristicCategory) => (
    <div className={cn.categoryTooltipContent}>
      <div>
        <strong>Код:</strong> {category.code}
      </div>
      <div>
        <strong>Родитель:</strong>{' '}
        {category.parent?.description ?? 'Без родителя (корневая категория)'}
      </div>
    </div>
  )

  const renderCategoryTag = (category: BoundCharacteristicCategory) => (
    <Tooltip key={category.id} title={renderCategoryTooltip(category)}>
      <div className={cn.categoryTag}>
        <span className={cn.categoryTagText}>{category.description}</span>
        <button
          type="button"
          className={cn.categoryTagRemove}
          disabled={isLockedApprovedCategories || isBusy}
          onClick={() => handleRemoveCategory(category.id)}
        >
          ×
        </button>
      </div>
    </Tooltip>
  )

  const handleCategoryTagsScroll = (direction: 'left' | 'right') => {
    const scrollNode = categoryTagsScrollRef.current
    if (!scrollNode) return

    scrollNode.scrollBy({
      left: direction === 'right' ? 260 : -260,
      behavior: 'smooth',
    })
  }

  const modalFooter = (
    <div className={cn.footer}>
      <Button className={cn.secondaryButton} onClick={onClose}>
        Отменить
      </Button>
      {!isReadOnly && (
        <Button
          className={cn.primaryButton}
          disabled={
            (!isDirty &&
              !isCategoriesDirty &&
              !isStatusDirty &&
              !isSlotConfigDirty) ||
            isBusy
          }
          loading={actionKey === 'save'}
          onClick={() => void handleSave()}
        >
          Сохранить
        </Button>
      )}
    </div>
  )

  return (
    <>
      {contextHolder}
      <Modal
        centered
        destroyOnHidden
        closable={false}
        title={<span className={cn.modalTitle}>Карточка характеристики</span>}
        open={open}
        footer={modalFooter}
        onCancel={onClose}
        width={860}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 260px)',
            overflowX: 'hidden',
            overflowY: 'auto',
          },
          footer: { paddingTop: 12 },
        }}
      >
        {loading ? (
          <div className={cn.modalSkeleton}>
            <div className={cn.skeletonMetaGrid}>
              <div className={cn.skeletonCard} />
              <div className={cn.skeletonCard} />
              <div className={cn.skeletonCard} />
            </div>
            <div className={cn.skeletonFieldBlock}>
              <div className={cn.skeletonLabel} />
              <div className={cn.skeletonInput} />
            </div>
            <div className={cn.skeletonFieldBlock}>
              <div className={cn.skeletonCheckbox} />
            </div>
            <div className={cn.skeletonSection}>
              <div className={cn.skeletonSectionTitle} />
              <div className={cn.skeletonInputRow}>
                <div className={cn.skeletonInput} />
                <div className={cn.skeletonButton} />
              </div>
              <div className={cn.skeletonList}>
                <div className={cn.skeletonListItem} />
                <div className={cn.skeletonListItem} />
                <div className={cn.skeletonListItem} />
              </div>
            </div>
            <div className={cn.skeletonFooter}>
              <div className={cn.skeletonButton} />
              <div className={cn.skeletonButton} />
            </div>
          </div>
        ) : (
          <div className={cn.modalContent}>
            <div className={[cn.meta, cn.characteristicMeta].join(' ')}>
              <div className={cn.metaRow}>
                <span className={cn.metaLabel}>Код:</span>
                <span>{item.code}</span>
              </div>
              <div className={cn.metaRow}>
                <span className={cn.metaLabel}>Статус:</span>
                {!isReadOnly && isApprover ? (
                  <Select
                    size="small"
                    className={[
                      cn.statusSelect,
                      STATUS_SELECT_CLASS[status],
                    ].join(' ')}
                    value={status}
                    disabled={isBusy}
                    options={APPROVAL_STATUSES.map((option) => ({
                      value: option,
                      label: CHARACTERISTIC_STATUS_LABELS[option],
                    }))}
                    onChange={(value) => setStatus(value)}
                  />
                ) : (
                  <span>{getStatusBadge(item.status) ?? 'Утверждена'}</span>
                )}
              </div>
              <div className={cn.metaRow}>
                <span className={cn.metaLabel}>Тип:</span>
                <span>{TYPE_LABELS[item.valueType] ?? item.valueType}</span>
              </div>
            </div>

            <div className={cn.form}>
              <div className={cn.field}>
                <label>Наименование</label>
                <input
                  value={description}
                  readOnly={isLockedApprovedDescription}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              {canUseUnitOfMeasurement && (
                <div className={cn.checkboxField}>
                  <Checkbox
                    checked={useUnitOfMeasurement}
                    disabled={isLockedApprovedUnitToggle}
                    onChange={(e) => {
                      setUseUnitOfMeasurement(e.target.checked)
                      if (!e.target.checked) setUnitOfMeasurementId('')
                    }}
                  >
                    Использовать единицу измерения
                  </Checkbox>
                </div>
              )}
              {canUseUnitOfMeasurement && useUnitOfMeasurement && (
                <div className={cn.field}>
                  <label>Единица измерения</label>
                  <Select
                    showSearch
                    value={unitOfMeasurementId || undefined}
                    disabled={isBusy || isReadOnly}
                    optionFilterProp="label"
                    options={units.map((unit) => ({
                      value: unit.id,
                      label: unit.description,
                    }))}
                    onChange={(value) => setUnitOfMeasurementId(value)}
                  />
                </div>
              )}
              <Tabs
                key={item.id}
                className={cn.detailsTabs}
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                items={[
                  {
                    key: 'categories',
                    label: 'Привязанные категории',
                    children: (
                      <div className={cn.tabPanel}>
                        {categories.length > 0 ? (
                          <div className={cn.categoryTagsSection}>
                            <button
                              type="button"
                              className={[
                                cn.categoryTagsScrollButton,
                                cn.categoryTagsScrollButtonLeft,
                              ].join(' ')}
                              disabled={
                                !categoryTagsOverflowing ||
                                !canScrollCategoryTagsLeft
                              }
                              onClick={() => handleCategoryTagsScroll('left')}
                            >
                              {'<'}
                            </button>
                            <div
                              ref={categoryTagsScrollRef}
                              className={cn.categoryTagsScroller}
                            >
                              <div className={cn.categoryTagsContent}>
                                <div className={cn.categoryTagsRow}>
                                  {splitCategoryRows.firstRow.map((category) =>
                                    renderCategoryTag(category)
                                  )}
                                </div>
                                {splitCategoryRows.secondRow.length > 0 && (
                                  <div className={cn.categoryTagsRow}>
                                    {splitCategoryRows.secondRow.map(
                                      (category) => renderCategoryTag(category)
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={[
                                cn.categoryTagsScrollButton,
                                cn.categoryTagsScrollButtonRight,
                              ].join(' ')}
                              disabled={
                                !categoryTagsOverflowing ||
                                !canScrollCategoryTagsRight
                              }
                              onClick={() => handleCategoryTagsScroll('right')}
                            >
                              {'>'}
                            </button>
                          </div>
                        ) : (
                          <div className={cn.referenceHint}>
                            Категории пока не привязаны.
                          </div>
                        )}
                        <CharacteristicCategoryBindingsModal
                          selectedCategories={categories}
                          onChange={setCategories}
                          readOnly={isReadOnly || isLockedApprovedCategories}
                        />
                      </div>
                    ),
                  },
                  ...(isSlottedCharacteristic
                    ? [
                        {
                          key: 'slotConfig',
                          label: 'Лимиты присоединений по категориям',
                          children: (
                            <div className={cn.tabPanel}>
                              <CharacteristicSlotConfigSection
                                ref={slotConfigSectionRef}
                                characteristicId={item.id}
                                boundCategories={categories}
                                readOnly={
                                  isReadOnly || isLockedApprovedCategories
                                }
                                onDirtyChange={setIsSlotConfigDirty}
                              />
                            </div>
                          ),
                        },
                      ]
                    : []),
                  ...(isReferenceCharacteristic
                    ? [
                        {
                          key: 'values',
                          label: 'Значения',
                          children: (
                            <div className={cn.tabPanel}>
                              {!isReadOnly && (
                                <div className={cn.valueCreateRow}>
                                  <input
                                    value={newValue}
                                    placeholder="Новое значение"
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                  />
                                  <Button
                                    className={cn.primaryButton}
                                    loading={valueActionKey === 'create'}
                                    onClick={() => void handleAddValue()}
                                  >
                                    Добавить
                                  </Button>
                                </div>
                              )}

                              <input
                                className={cn.valueSearch}
                                value={valueSearch}
                                placeholder="Поиск значений"
                                onChange={(e) => setValueSearch(e.target.value)}
                              />

                              {!itemsLoaded ? (
                                <div className={cn.referenceHint}>
                                  {itemsLoading
                                    ? 'Загрузка значений...'
                                    : 'Значения не загружены.'}
                                </div>
                              ) : filteredItems.length > 0 ? (
                                <div className={cn.valuesList}>
                                  {filteredItems.map((dictionaryItem) => {
                                    const isEditing =
                                      editingValueId === dictionaryItem.id
                                    const isUpdating =
                                      valueActionKey ===
                                      `update-${dictionaryItem.id}`
                                    const isDeleting =
                                      valueActionKey ===
                                      `delete-${dictionaryItem.id}`

                                    return (
                                      <div
                                        key={dictionaryItem.id}
                                        className={cn.valueItemRow}
                                      >
                                        {isEditing && !isReadOnly ? (
                                          <>
                                            <input
                                              value={editingValue}
                                              onChange={(e) =>
                                                setEditingValue(e.target.value)
                                              }
                                            />
                                            <div className={cn.valueActions}>
                                              <Button
                                                className={cn.primaryButton}
                                                loading={isUpdating}
                                                onClick={() =>
                                                  void handleSaveValue()
                                                }
                                              >
                                                Сохранить
                                              </Button>
                                              <Button
                                                className={cn.secondaryButton}
                                                onClick={() => {
                                                  setEditingValueId('')
                                                  setEditingValue('')
                                                }}
                                              >
                                                Отмена
                                              </Button>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div
                                              className={cn.valueItemTextWrap}
                                            >
                                              <span
                                                className={cn.valueItemText}
                                              >
                                                {dictionaryItem.value}
                                              </span>
                                            </div>
                                            {!isReadOnly && (
                                              <div className={cn.valueActions}>
                                                <Button
                                                  className={cn.secondaryButton}
                                                  onClick={() => {
                                                    setEditingValueId(
                                                      dictionaryItem.id
                                                    )
                                                    setEditingValue(
                                                      dictionaryItem.value
                                                    )
                                                  }}
                                                >
                                                  Изменить
                                                </Button>
                                                <Button
                                                  className={cn.dangerButton}
                                                  loading={isDeleting}
                                                  onClick={() =>
                                                    void handleDeleteValue(
                                                      dictionaryItem.id
                                                    )
                                                  }
                                                >
                                                  Удалить
                                                </Button>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                <div className={cn.referenceHint}>
                                  {items.length > 0
                                    ? 'Значения не найдены.'
                                    : 'Отсутствуют значения характеристик.'}
                                </div>
                              )}
                            </div>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default CharacteristicDetailsModal
